import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Sparkles, Lock, Zap, Sun, Moon, Heart, Activity, 
  Waves, Circle, Eye, Flame, CloudRain, Brain
} from 'lucide-react';
import { useEnergeticCorrelations } from '@/hooks/useEnergeticCorrelations';
import { useSubscription, PRODUCTS } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

// Metric definitions for energetic tracking
const VIBRATIONAL_METRICS = [
  { key: 'frequency', label: 'Vibrational Frequency', icon: Waves, color: 'hsl(var(--chart-1))' },
  { key: 'reading_quality', label: 'Reading Quality', icon: Eye, color: 'hsl(var(--chart-2))' },
];

const CHAKRA_METRICS = [
  { key: 'energy_before', label: 'Energy Before Session', icon: Circle, color: 'hsl(var(--chart-3))' },
  { key: 'energy_after', label: 'Energy After Session', icon: Sun, color: 'hsl(var(--chart-4))' },
];

const DATE_RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

// Reflective blurbs for energetic metrics
const METRIC_REFLECTIONS: Record<string, string> = {
  frequency: "Your vibrational frequency reflects your overall energetic state. Higher frequencies often correlate with feelings of lightness, clarity, and connection.",
  reading_quality: "Reading quality indicates how clear or grounded you felt during your frequency check-in. Clearer readings may emerge with consistent practice.",
  energy_before: "Your energy level before chakra work gives insight into where you're starting from. Lower starting points can show more dramatic shifts.",
  energy_after: "Post-session energy levels show how your system responds to chakra healing. Consistent lifts may indicate this practice supports you.",
};

interface TimeSeriesData {
  date: string;
  value: number | null;
  displayDate: string;
}

interface ChakraSessionData {
  date: string;
  energyBefore: number;
  energyAfter: number;
  displayDate: string;
}

export function EnergeticPatternsExplorer() {
  const { correlations, isLoading: correlationsLoading, error } = useEnergeticCorrelations(60);
  const { productId, hasPremiumAccess } = useSubscription();
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState<'vibrational' | 'chakra'>('vibrational');
  const [selectedMetric, setSelectedMetric] = useState<string>('frequency');
  const [dateRange, setDateRange] = useState<string>('30');
  const [vibrationalData, setVibrationalData] = useState<TimeSeriesData[]>([]);
  const [chakraData, setChakraData] = useState<ChakraSessionData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const isSanctuary = productId === PRODUCTS.MASTERY || hasPremiumAccess();

  // Fetch vibrational frequency data
  useEffect(() => {
    const fetchVibrationalData = async () => {
      if (selectedCategory !== 'vibrational') return;
      
      setIsLoadingData(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingData(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('vibrational_frequency_log')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', `${startDate}T00:00:00.000Z`)
        .order('created_at', { ascending: true });

      if (!error && data) {
        const formattedData = data.map(row => ({
          date: row.created_at,
          value: selectedMetric === 'frequency' ? row.frequency_level : row.reading_quality_today,
          displayDate: format(new Date(row.created_at), 'MMM d')
        }));
        setVibrationalData(formattedData);
      } else {
        setVibrationalData([]);
      }
      
      setIsLoadingData(false);
    };

    if (isSanctuary) {
      fetchVibrationalData();
    }
  }, [selectedMetric, dateRange, selectedCategory, isSanctuary]);

  // Fetch chakra session data
  useEffect(() => {
    const fetchChakraData = async () => {
      if (selectedCategory !== 'chakra') return;
      
      setIsLoadingData(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingData(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('chakra_healing_sessions')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', `${startDate}T00:00:00.000Z`)
        .order('created_at', { ascending: true });

      if (!error && data) {
        const formattedData = data.map(row => ({
          date: row.created_at,
          energyBefore: row.energy_before || 0,
          energyAfter: row.energy_after || 0,
          displayDate: format(new Date(row.created_at), 'MMM d')
        }));
        setChakraData(formattedData);
      } else {
        setChakraData([]);
      }
      
      setIsLoadingData(false);
    };

    if (isSanctuary) {
      fetchChakraData();
    }
  }, [dateRange, selectedCategory, isSanctuary]);

  // Gate behind Sanctuary subscription
  if (!isSanctuary) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Energetic Patterns & Spiritual Alignment</CardTitle>
          <CardDescription>
            Sanctuary members get access to energetic correlations - connecting your vibrational frequency, chakra work, and spiritual practices with emotional wellbeing.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Waves className="w-4 h-4" />
              <span>Vibrational Frequency ↔ Mood correlations</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Circle className="w-4 h-4" />
              <span>Chakra Healing ↔ Energy & Emotional shifts</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Sun className="w-4 h-4" />
              <span>Sitting in the Power ↔ Spiritual Connection</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Zap className="w-4 h-4" />
              <span>Energy Body ↔ Emotional State tracking</span>
            </div>
          </div>
          <Button onClick={() => navigate('/subscription')} className="w-full">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Sanctuary
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentMetrics = selectedCategory === 'vibrational' ? VIBRATIONAL_METRICS : CHAKRA_METRICS;
  const currentMetricInfo = currentMetrics.find(m => m.key === selectedMetric) || currentMetrics[0];
  const MetricIcon = currentMetricInfo.icon;

  const hasVibrationalData = vibrationalData.some(d => d.value !== null);
  const hasChakraData = chakraData.length > 0;

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Energetic Patterns & Spiritual Alignment
          </CardTitle>
          <CardDescription>
            Explore correlations between your energetic practices and overall wellbeing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={(v) => {
            setSelectedCategory(v as 'vibrational' | 'chakra');
            setSelectedMetric(v === 'vibrational' ? 'frequency' : 'energy_before');
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="vibrational" className="flex items-center gap-2">
                <Waves className="w-4 h-4" />
                Vibrational Frequency
              </TabsTrigger>
              <TabsTrigger value="chakra" className="flex items-center gap-2">
                <Circle className="w-4 h-4" />
                Chakra Sessions
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Metric & Date Range Selectors */}
          <div className="flex flex-col sm:flex-row gap-3">
            {selectedCategory === 'vibrational' && (
              <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select metric" />
                </SelectTrigger>
                <SelectContent>
                  {VIBRATIONAL_METRICS.map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <SelectItem key={metric.key} value={metric.key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {metric.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}

            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className={selectedCategory === 'vibrational' ? "w-full sm:w-40" : "flex-1"}>
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Time Series Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <MetricIcon className="w-5 h-5" style={{ color: currentMetricInfo.color }} />
              </div>
              <div>
                <CardTitle className="text-base">
                  {selectedCategory === 'vibrational' ? currentMetricInfo.label : 'Chakra Session Energy'} Over Time
                </CardTitle>
                <CardDescription className="text-xs">
                  {DATE_RANGES.find(r => r.value === dateRange)?.label}
                </CardDescription>
              </div>
            </div>
            {(selectedCategory === 'vibrational' ? hasVibrationalData : hasChakraData) && (
              <Badge variant="secondary" className="text-xs">
                {selectedCategory === 'vibrational' 
                  ? `${vibrationalData.filter(d => d.value !== null).length} entries`
                  : `${chakraData.length} sessions`
                }
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingData ? (
            <Skeleton className="h-48 w-full" />
          ) : selectedCategory === 'vibrational' && hasVibrationalData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={vibrationalData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={currentMetricInfo.color}
                    strokeWidth={2}
                    dot={{ fill: currentMetricInfo.color, strokeWidth: 0, r: 3 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : selectedCategory === 'chakra' && hasChakraData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chakraData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    domain={[0, 10]}
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="energyBefore" 
                    stroke="hsl(var(--chart-3))"
                    fill="hsl(var(--chart-3))"
                    fillOpacity={0.3}
                    name="Energy Before"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="energyAfter" 
                    stroke="hsl(var(--chart-4))"
                    fill="hsl(var(--chart-4))"
                    fillOpacity={0.3}
                    name="Energy After"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <div className="text-center p-6 rounded-lg bg-muted/30">
                <Waves className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  {selectedCategory === 'vibrational' 
                    ? 'No vibrational frequency data logged yet.'
                    : 'No chakra healing sessions logged yet.'
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Start tracking to see your patterns emerge.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reflection Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Eye className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1">Understanding This Metric</h4>
              <p className="text-xs text-muted-foreground">
                {METRIC_REFLECTIONS[selectedMetric] || METRIC_REFLECTIONS['frequency']}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Correlations Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Energetic Correlations
          </CardTitle>
          <CardDescription>
            Patterns emerging from your energetic and spiritual practice data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {correlationsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : correlations.length > 0 ? (
            <div className="space-y-3">
              {correlations.map((correlation, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-lg border bg-card/50 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {correlation.metric_a} ↔ {correlation.metric_b}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {correlation.sample_size} data points
                    </span>
                  </div>
                  <p className="text-sm text-foreground/80">
                    {correlation.interpretation}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                No correlations detected yet.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Continue logging vibrational frequency, chakra sessions, and daily check-ins to discover patterns.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guidance Card */}
      <Card className="border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Heart className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h4 className="text-sm font-medium mb-1">A Gentle Reminder</h4>
              <p className="text-xs text-muted-foreground">
                These reflections are offered with curiosity, not certainty. They're drawn from your personal practice data and are not meant to be prescriptions or judgments. Trust your own inner knowing - your inner wisdom is the ultimate guide to what supports your energetic wellbeing.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
